import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {BehaviorSubject, catchError, Observable} from 'rxjs';

import {BasedataService} from '../shared/basedata.service';

import {
  Structure,
  StructureCategories,
  StructureCategory,
  StructurePermission,
  StructurePermissions,
  Structures
} from './structure';
import {MediatorService} from '@mydocma/core';
import {ConfigService} from '@docma-mm-core/config/config.service';
import {constants} from '@docma-mm-core/constants';
import {
  Project,
  ProjectService,
  ProjectStructureNode,
  ProjectStructureService,
  ProjectStructureTree
} from '@mydocma/project';
import {Logger} from '@mydocma/common/src/lib/logger';

const selectedStructureKey = 'mm-selectedStructure';

@Injectable()
export class StructureService extends BasedataService {

  constructor(private http: HttpClient,
              private mediator: MediatorService,
              configService: ConfigService,
              private projectStructureService: ProjectStructureService,
              private projectService: ProjectService,
              private readonly logger: Logger) {
    super(configService);

    this.projectService.currentProject.subscribe(p => this.currentProject = p);
  }


  private projectUrl = '/projects/';
  private structureUrl = '/structures';
  private structureCategoryUrl = '/structurecategories';
  private permissionUrl = '/permissions';

  private currentProject: Project;

  structuresDictSubject = new BehaviorSubject<{ [Id: number]: Structure }>({});
  structuresDict$ = this.structuresDictSubject.asObservable();

  private getProjectStructureUrl(projectId: number) {
    return super.getFullUrl(this.projectUrl + projectId + this.structureUrl);
  }

  private getStructureUrl() {
    return super.getFullUrl(this.structureUrl);
  }

  private getStructureCategoryUrl() {
    return super.getFullUrl(this.structureCategoryUrl);
  }

  private getStructurePermissionUrl() {
    return super.getFullUrl(this.structureUrl + this.permissionUrl);
  }

  setSelectedStructure(structure: Structure) {
    const selectedStructure = localStorage.getItem(selectedStructureKey);
    if (selectedStructure) {
      localStorage.removeItem(selectedStructureKey);
    }

    localStorage.setItem(selectedStructureKey, JSON.stringify(structure));
    this.mediator.publish('structureFilterChanged', 'StructureService', structure);
  }




  loadStructureTree(structures: Structure[]): Structure[] {
    if (!structures || structures.length === 0) {
      return structures;
    }

    const structuresDict: { [Id: number]: Structure } = {};
    const structuresByParentDict: { [Id: number]: Structure[] } = {};

    structures.forEach(st => {
      structuresDict[st.Id] = st;
    });

    structures.forEach(st => {
      if (!structuresByParentDict.hasOwnProperty(st.Parent.Id)) {
        structuresByParentDict[st.Parent.Id] = [];
      }
      structuresByParentDict[st.Parent.Id].push(structuresDict[st.Id]);
    });

    const tree = [];
    const first = new Structure(structures[0]);
    first.StructurePath = first.Name1 + ' - ' + first.Name2;
    first.StructurePathShort = first.Name1;
    first.StructurePathLong = first.Name2;
    tree.push(this.loadStructureNode(first, structuresByParentDict));

    const projectStructureTree = this.convertMmToProjectStructureTree(tree[0], new Map<string, ProjectStructureNode>());
    projectStructureTree.parent = null;
    if (this.currentProject) { projectStructureTree.projectId = this.currentProject.id; }
    this.projectStructureService.currentProjectStructureTree = projectStructureTree;

    this.structuresDictSubject.next(structuresDict);

    return tree;
  }

  private loadStructureNode(structure: Structure, structuresByParent: {[Id: number]: Structure[]}): Structure {
    try {
      if (structuresByParent.hasOwnProperty(structure.Id)) {
        const children = structuresByParent[structure.Id]
          .map(st => {
            const child = new Structure(st);
            child.StructurePath = structure.StructurePath + ' \\ ' + child.Name1 + ' - ' + child.Name2;
            child.StructurePathShort = structure.StructurePathShort + ' \\ ' + child.Name1;
            child.StructurePathLong = structure.StructurePathLong + ' \\ ' + child.Name2;
            return this.loadStructureNode(child, structuresByParent);
          });
        structure.HasChildren = children?.length > 0;
        structure.Children = children;
      }
    } catch (err) {
      this.logger.error(err, '', 'StructureService');
    }
    return structure;
  }

  // get structures by structure id
  getStructures(structureId: number, top: number = constants.MaxInt, skip: number = 0, level: number = 1): Observable<Structures> {
    const endpoint = this.getStructureUrl() + '/' + structureId;
    let httpParams = new HttpParams();
    if (top) {
      httpParams = httpParams.set('$top', top.toString());
    }
    if (skip) {
      httpParams = httpParams.set('$skip', skip.toString());
    }
    if (level) {
      httpParams = httpParams.set('level', level.toString());
    }
    return this.http
      .get<Structures>(endpoint, {headers: this.getHeaders(), params: httpParams})
      .pipe(catchError(e => this.handleError(e)));
  }

  // get structures by project
  getProjectStructures(projectId: number): Observable<Structures> {
    const endpoint = this.getProjectStructureUrl(projectId);

    let httpParams = new HttpParams();
    httpParams = httpParams.set('$top', `${constants.MaxInt}`);
    httpParams = httpParams.set('$skip', '0');
    httpParams = httpParams.set('additionalFields', 8);

    return this.http
      .get<Structures>(endpoint, {headers: this.getHeaders(), params: httpParams})
      .pipe(catchError(e => this.handleError(e)));
  }

  // create structure
  createStructure(structure: Structure): Observable<Structure> {
    const endpoint = this.getStructureUrl();
    return this.http
      .post<Structure>(endpoint, JSON.stringify(structure), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get structure categories
  getStructureCategories(top: number = 1000, skip: number = 0): Observable<StructureCategories> {
    const endpoint = this.getStructureCategoryUrl();
    let httpParams = new HttpParams();
    if (top) {
      httpParams = httpParams.set('$top', top.toString());
    }
    if (skip) {
      httpParams = httpParams.set('$skip', skip.toString());
    }
    return this.http
      .get<StructureCategories>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get structure category by id
  getStructureCategoryById(structureCategoryId: number): Observable<StructureCategory> {
    const endpoint = this.getStructureCategoryUrl() + '/' + structureCategoryId;
    return this.http
      .get<StructureCategory>(endpoint, { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get structure category by uid
  getStructureCategoryByUid(structureCategoryUid: string): Observable<StructureCategory> {
    const endpoint = this.getStructureCategoryUrl();
    let httpParams = new HttpParams();
    httpParams = httpParams.set('uid', structureCategoryUid);
    return this.http
      .get<StructureCategory>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // create new structure category
  createStructureCategory(structureCategory: StructureCategory): Observable<StructureCategory> {
    const endpoint = this.getStructureCategoryUrl();
    return this.http
      .post<StructureCategory>(endpoint, JSON.stringify(structureCategory), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // update existing structure category
  updateStructureCategory(structureCategoryId: number, structureCategory: StructureCategory): Observable<StructureCategory> {
    const endpoint = this.getStructureCategoryUrl() + '/' + structureCategoryId;
    return this.http
      .put<StructureCategory>(endpoint, JSON.stringify(structureCategory), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // delete structure category
  deleteStructureCategory(structureCategoryId: number): Observable<any> {
    const endpoint = this.getStructureCategoryUrl();
    return this.http
      .delete<any>(endpoint, { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get structure permissions
  getStructurePermissions(top: number = 1000, skip: number = 0, level: number = 1): Observable<StructurePermissions> {
    const endpoint = this.getStructurePermissionUrl();
    let httpParams = new HttpParams();
    if (top) {
      httpParams = httpParams.set('$top', top.toString());
    }
    if (skip) {
      httpParams = httpParams.set('$skip', skip.toString());
    }
    if (level) {
      httpParams = httpParams.set('level', level.toString());
    }
    return this.http
      .get<StructurePermissions>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // create structure permission
  createStructurePermission(structurePermission: StructurePermission): Observable<StructurePermission> {
    const endpoint = this.getStructurePermissionUrl();
    return this.http
      .post<StructurePermission>(endpoint, JSON.stringify(structurePermission), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // delete structure permission
  deleteStructurePermission(structurePermissionId: number): Observable<any> {
    const endpoint = this.getStructurePermissionUrl() + '/' + structurePermissionId;
    return this.http
      .delete<any>(endpoint, { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  /**
   * Converts the properties that BP requires to work from uppercase to lowercase.
   */
  private convertMmToProjectStructureTree(mmTree: Structure, cache: Map<string, ProjectStructureTree>): ProjectStructureTree {

    const node = (mmTree as any as ProjectStructureNode);

    node.parent = cache.get(mmTree.Parent?.Id.toString());
    node.name = mmTree.Name1;
    node.desc = mmTree.Name2;
    node.path = mmTree.StructurePath;
    node.level = mmTree.Level;
    node.id = mmTree.RootId == mmTree.Id ? this.currentProject.id : mmTree.Id.toString();
    cache.set(mmTree.Id.toString(), node);

    if (mmTree.Children) {
      node.children = mmTree.Children.map((c) => this.convertMmToProjectStructureTree(c, cache));
    }

    return node;
  }

}
